<template>
    <div>
        <!--title-->
        <div>
            <strong class="color-primary font-size-20">
                {{entry.topic_title}}
            </strong>

            <router-link :to="{name: 'training-topic-view', params: {id: entry.topic_id}}">
                <i class="material-icons pointer margin-bottom-5" aria-hidden="true" aria-label="Fortbildungsthema aufrufen" v-tooltip="'Fortbildungsthema aufrufen'" role="img">
                    open_in_new
                </i>
            </router-link>

            <span class="tag-emphasized margin-left-5" :class="entry.topic_active ? 'tag-active' : 'tag-inactive'">
                {{entry.topic_active ? 'Aktiv' : 'Inaktiv'}}
            </span>
        </div>

        <!--date & address-->
        <div class="row">
            <div class="col-md-4">
                <strong>Datum:&nbsp;</strong>
                {{dateText}}
            </div>

            <div class="col-md-3">
                <strong>Typ:&nbsp;</strong>
                <template v-if="entry.type === appointmentTypes.online">Online</template>
                <template v-else-if="entry.room_id === appointmentLocationTypes.internal">Interner Ort</template>
                <template v-else-if="entry.room_id === appointmentLocationTypes.external">Externer Ort</template>
                <template v-else>Ort</template>
            </div>

            <div v-if="entry.address_description" class="col-md-5">
                <strong>Standort:&nbsp;</strong>
                {{entry.address_description}}
            </div>
        </div>

        <!--location-->
        <div class="margin-top-5">
            <strong>Lokalität:&nbsp;</strong>
            <template v-if="entry.room_title">
                <i class="material-icons md-16" aria-hidden="true" role="img">location_on</i>
                <router-link :title="entry.room_title"
                    :to="{name: 'roommanagement-list', params: {id: entry.room_id, type: 'week', date: roomLinkDateParameter }}">
                    {{entry.room_title}}
                </router-link>
            </template>
            <template v-else-if="entry.room_location_description">
                <i class="material-icons md-16" aria-hidden="true" role="img">location_on</i>
                {{entry.room_location_description}}
            </template>
            <template v-else-if="entry.online_location_description">{{entry.online_location_description}}</template>
        </div>

        <div class="row margin-top-5">
            <!--mandatory-->
            <div class="col-md-4">
                <div class="flex text-overflow">
                    <strong>Teilnahmebedingungen:</strong>
                    <div class="margin-left-5">
                            <span v-if="entry.mandatory" class="flex-vertical-middle" title="Pflicht">
                                <i class="material-icons md-16 text-warning margin-right-2" aria-hidden="true" role="img">circle</i>
                                Pflicht
                            </span>
                        <span v-else class="flex-vertical-middle" title="Freiwillig">
                                <i class="material-icons md-16 text-success margin-right-2" aria-hidden="true" role="img">trip_origin</i>
                                Freiwillig
                            </span>
                    </div>
                </div>
            </div>

            <!--categories-->
            <div class="col-md-8">
                <div class="flex text-overflow">
                    <strong>Kategorie(n):</strong>
                    <div class="margin-left-5">
                        <template v-for="categoryId in entry.topic_categories">
                            <span v-if="categoriesAssoc[categoryId]" class="colour-badge medium margin-right-5 text-overflow">
                                <span class="colour-badge-preview" :style="{backgroundColor: '#' + categoriesAssoc[categoryId].color}"/>
                                <span class="colour-badge-text text-overflow" v-text="categoriesAssoc[categoryId].name"/>
                            </span>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <hr class="margin-top-15 margin-bottom-15">

        <!--contacts-->
        <div :class="{'margin-bottom-10': hasContacts}">
            <strong>Ansprechpartner</strong>
        </div>

        <div v-if="hasContacts" class="flex flex-wrap">
            <div v-for="employeeId in entry.contacts" class="margin-bottom-10 margin-right-10">
                <person-box-employee :id="employeeId" :key="'training-appointment-popup-view-employee-' + employeeId"/>
            </div>
        </div>
        <template v-else>Kein Ansprechpartner vorhanden</template>
    </div>
</template>

<script lang="ts">
    import {CategoryManager} from '@/library/category';
    import {appointmentLocationTypes, appointmentTypes} from '@/library/internalExternalAppointment';
    import {Component, Prop, Vue} from 'vue-facing-decorator';
    import PersonBoxEmployee from '../../../../components/PersonBoxEmployee.vue';
    import dateHelper from '../../../../utils/portuna/dateHelper';

    @Component({
        name: 'TrainingAppointmentPopupView',
        components: {PersonBoxEmployee},
    })
    export default class TrainingAppointmentPopupView extends Vue
    {
        @Prop({
            type: CategoryManager,
            required: true,
        }) categoryManager: CategoryManager;

        @Prop({
            type: Object,
            required: true,
        }) entry: TrainingAppointmentPopupViewEntry;

        appointmentTypes = appointmentTypes;

        appointmentLocationTypes = appointmentLocationTypes;

        get dateText(): string
        {
            let text = dateHelper('d.m.Y', this.entry.date_start) + ', ' + this.entry.time_start + ' Uhr - ';

            if (this.entry.date_start !== this.entry.date_end)
            {
                text += dateHelper('d.m.Y', this.entry.date_end) + ', ';
            }

            text += this.entry.time_end + ' Uhr';

            return text;
        }

        get roomLinkDateParameter(): string
        {
            return this.entry.date_start + ' ' + this.entry.time_start + ':00';
        }

        get hasContacts(): boolean
        {
            return this.entry.contacts && this.entry.contacts.length > 0;
        }

        get categoriesAssoc(): Record<string, ICategory>
        {
            return this.categoryManager.categoriesAssoc();
        }
    }
</script>